import React, { useState, useEffect } from "react";
import axios from "axios";
import { CiEdit } from "react-icons/ci";
import { RiDeleteBin6Line } from "react-icons/ri";

const BASE_URL = "https://staging.pteshstudios.com";

const resolveSrc = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value.startsWith("http") ? value : `${BASE_URL}/${value}`;
  return null;
};

export default function AdminPartnership() {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Edit state
  const [editingPartner, setEditingPartner] = useState(null);
  const [editText, setEditText] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  // Delete state
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Add state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newText, setNewText] = useState("");
  const [newImage, setNewImage] = useState(null);
  const [newOrder, setNewOrder] = useState("");
  const [adding, setAdding] = useState(false);

  // ── GET all partners ──
  const fetchPartners = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const { data } = await axios.get(`${BASE_URL}/api/partners`);
      console.log("Partners response:", data);
      const payload = data?.data ?? data;
      const list = Array.isArray(payload) ? payload : [];
      setLogos(list);
    } catch (err) {
      console.error("Failed to fetch partners:", err);
      setFetchError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPartners(); }, []);

  // ── POST — add partner ──
  const handleAddSave = async () => {
    if (!newImage) return;
    setAdding(true);
    try {
      const form = new FormData();
      form.append("text", newText);
      form.append("order", newOrder || logos.length + 1);
      form.append("image", newImage);

      console.log("Adding partner:");
      for (let [k, v] of form.entries()) console.log(`  ${k}:`, v);

      await axios.post(`${BASE_URL}/api/partners`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setShowAddModal(false);
      setNewText("");
      setNewImage(null);
      setNewOrder("");
      fetchPartners();
    } catch (err) {
      console.error("Failed to add partner:", err);
    } finally {
      setAdding(false);
    }
  };

  // ── PUT — update partner ──
  const handleEditSave = async () => {
    if (!editingPartner) return;
    setEditSaving(true);
    try {
      const form = new FormData();
      form.append("text", editText);
      form.append("order", editingPartner.order ?? 0);
      if (editImage instanceof File) form.append("image", editImage);

      console.log("Updating partner:", editingPartner._id);
      for (let [k, v] of form.entries()) console.log(`  ${k}:`, v);

      await axios.put(`${BASE_URL}/api/partners/${editingPartner._id}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditingPartner(null);
      setEditText("");
      setEditImage(null);
      fetchPartners();
    } catch (err) {
      console.error("Failed to update partner:", err);
    } finally {
      setEditSaving(false);
    }
  };

  // ── DELETE — remove partner ──
  const handleDeleteConfirm = async (id) => {
    setDeleting(true);
    try {
      await axios.delete(`${BASE_URL}/api/partners/${id}`);
      setDeleteConfirmId(null);
      fetchPartners();
    } catch (err) {
      console.error("Failed to delete partner:", err);
    } finally {
      setDeleting(false);
    }
  };

  const handleEditClick = (logo) => {
    setEditingPartner(logo);
    setEditText(logo.text || "");
    setEditImage(null);
  };

  const handleAddCancel = () => {
    setShowAddModal(false);
    setNewText("");
    setNewImage(null);
    setNewOrder("");
  };

  return (
    <>
      <style>{styles}</style>
      <section className="ap__section">
        <div className="ap__header">
          <div>
            <h2 className="ap__title">Our Partners</h2>
            <p className="ap__subtitle">Manage your partner logos below</p>
          </div>
        </div>

        {loading && <p style={{ color: "#888", textAlign: "center" }}>Loading partners...</p>}
        {fetchError && <p style={{ color: "#e53935", textAlign: "center" }}>{fetchError}</p>}

        <div className="ap__grid">
          {logos.map((logo) => (
            <div className="ap__card" key={logo._id}>
              <div className="ap__img-wrap">
                <img
                  src={resolveSrc(logo.image) || ""}
                  alt={logo.text || "Partner"}
                  className="ap__img"
                />
              </div>
              <p className="ap__alt-label">{logo.text || "—"}</p>
              <div className="ap__actions">
                <button
                  className="ap__icon-btn ap__icon-btn--edit"
                  title="Edit"
                  onClick={() => handleEditClick(logo)}
                >
                  <CiEdit size={18} />
                </button>
                <button
                  className="ap__icon-btn ap__icon-btn--delete"
                  title="Delete"
                  onClick={() => setDeleteConfirmId(logo._id)}
                >
                  <RiDeleteBin6Line size={16} />
                </button>
              </div>
            </div>
          ))}

          {/* Add New Card */}
          <div className="ap__card ap__card--add" onClick={() => setShowAddModal(true)}>
            <div className="ap__add-icon">+</div>
            <p className="ap__add-label">Add New Partner</p>
          </div>
        </div>

        {/* ── Add Modal ── */}
        {showAddModal && (
          <div className="ap__modal-overlay" onClick={handleAddCancel}>
            <div className="ap__modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="ap__modal-title">Add New Partner</h3>

              <label className="ap__modal-label">Partner Name</label>
              <input
                className="ap__modal-input"
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="e.g. Shell Nigeria"
              />

              <label className="ap__modal-label">Order</label>
              <input
                className="ap__modal-input"
                type="number"
                value={newOrder}
                onChange={(e) => setNewOrder(e.target.value)}
                placeholder={`e.g. ${logos.length + 1}`}
              />

              <label className="ap__modal-label">Upload Logo <span className="ap__modal-required">*</span></label>
              <label className="ap__modal-upload-area">
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => setNewImage(e.target.files[0] || null)}
                />
                {newImage ? (
                  <img src={URL.createObjectURL(newImage)} alt="Preview" className="ap__modal-preview" />
                ) : (
                  <div className="ap__modal-upload-placeholder">
                    <span className="ap__modal-upload-icon">↑</span>
                    <span>Click to upload image</span>
                    <span className="ap__modal-upload-hint">PNG, JPG, SVG supported</span>
                  </div>
                )}
              </label>
              {newImage && <p className="ap__modal-filename">{newImage.name}</p>}

              <div className="ap__modal-actions">
                <button className="ap__modal-btn ap__modal-btn--cancel" onClick={handleAddCancel}>
                  Cancel
                </button>
                <button
                  className="ap__modal-btn ap__modal-btn--save"
                  onClick={handleAddSave}
                  disabled={!newImage || adding}
                  style={{ opacity: (!newImage || adding) ? 0.5 : 1, cursor: (!newImage || adding) ? "not-allowed" : "pointer" }}
                >
                  {adding ? "Adding..." : "Add Partner"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Edit Modal ── */}
        {editingPartner && (
          <div className="ap__modal-overlay" onClick={() => setEditingPartner(null)}>
            <div className="ap__modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="ap__modal-title">Edit Partner</h3>

              <label className="ap__modal-label">Partner Name</label>
              <input
                className="ap__modal-input"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="e.g. Shell Nigeria"
              />

              <label className="ap__modal-label">Replace Logo Image</label>
              <label className="ap__modal-upload-area">
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => setEditImage(e.target.files[0] || null)}
                />
                {editImage ? (
                  <img src={URL.createObjectURL(editImage)} alt="Preview" className="ap__modal-preview" />
                ) : editingPartner.image ? (
                  <img src={resolveSrc(editingPartner.image)} alt="Current" className="ap__modal-preview" />
                ) : (
                  <div className="ap__modal-upload-placeholder">
                    <span className="ap__modal-upload-icon">↑</span>
                    <span>Click to replace image</span>
                    <span className="ap__modal-upload-hint">PNG, JPG, SVG supported</span>
                  </div>
                )}
              </label>

              <div className="ap__modal-actions">
                <button className="ap__modal-btn ap__modal-btn--cancel" onClick={() => setEditingPartner(null)}>
                  Cancel
                </button>
                <button
                  className="ap__modal-btn ap__modal-btn--save"
                  onClick={handleEditSave}
                  disabled={editSaving}
                >
                  {editSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete Confirm Modal ── */}
        {deleteConfirmId && (
          <div className="ap__modal-overlay" onClick={() => setDeleteConfirmId(null)}>
            <div className="ap__modal ap__modal--sm" onClick={(e) => e.stopPropagation()}>
              <h3 className="ap__modal-title">Remove Partner?</h3>
              <p className="ap__modal-body">This action cannot be undone.</p>
              <div className="ap__modal-actions">
                <button className="ap__modal-btn ap__modal-btn--cancel" onClick={() => setDeleteConfirmId(null)}>
                  Cancel
                </button>
                <button
                  className="ap__modal-btn ap__modal-btn--delete"
                  onClick={() => handleDeleteConfirm(deleteConfirmId)}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
}

const styles = `
  .ap__section {
    width: 90%;
    margin: 60px auto;
    font-family: inherit;
  }

  .ap__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 32px;
    flex-wrap: wrap;
    gap: 12px;
  }

  .ap__title {
    font-size: 22px;
    font-weight: 700;
    color: #1a1a1a;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin: 0 0 6px;
  }

  .ap__subtitle {
    font-size: 13px;
    color: #888;
    margin: 0;
  }

  .ap__grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }

  @media (max-width: 900px) {
    .ap__grid { grid-template-columns: repeat(2, 1fr); }
  }

  @media (max-width: 500px) {
    .ap__grid { grid-template-columns: 1fr; }
  }

  .ap__card {
    background: #fff;
    border: 1.5px solid #ebebeb;
    border-radius: 12px;
    padding: 20px 16px 14px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    transition: box-shadow 0.2s, border-color 0.2s;
  }

  .ap__card:hover {
    box-shadow: 0 6px 24px rgba(0,0,0,0.08);
    border-color: #d0d0d0;
  }

  .ap__card--add {
    border: 2px dashed #d1d5db;
    background: #fafafa;
    cursor: pointer;
    justify-content: center;
    min-height: 160px;
    transition: border-color 0.2s, background 0.2s;
  }

  .ap__card--add:hover {
    border-color: #1d4ed8;
    background: #eff6ff;
    box-shadow: none;
  }

  .ap__add-icon {
    font-size: 32px;
    font-weight: 300;
    color: #9ca3af;
    line-height: 1;
    transition: color 0.2s;
  }

  .ap__card--add:hover .ap__add-icon { color: #1d4ed8; }

  .ap__add-label {
    font-size: 13px;
    color: #9ca3af;
    font-weight: 500;
    margin: 0;
    transition: color 0.2s;
  }

  .ap__card--add:hover .ap__add-label { color: #1d4ed8; }

  .ap__img-wrap {
    width: 100%;
    height: 90px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f9f9f9;
    border-radius: 8px;
    overflow: hidden;
    padding: 10px;
  }

  .ap__img {
    max-width: 100%;
    max-height: 70px;
    object-fit: contain;
  }

  .ap__alt-label {
    font-size: 12px;
    color: #666;
    text-align: center;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  }

  .ap__actions {
    display: flex;
    gap: 8px;
    margin-top: 4px;
  }

  .ap__icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 7px;
    border: 1.5px solid transparent;
    cursor: pointer;
    transition: all 0.18s;
    background: transparent;
  }

  .ap__icon-btn--edit {
    border-color: #d1d5db;
    color: #374151;
    background: #f9fafb;
  }

  .ap__icon-btn--edit:hover {
    background: #e0e7ff;
    border-color: #6366f1;
    color: #4f46e5;
  }

  .ap__icon-btn--delete {
    border-color: #fecaca;
    color: #ef4444;
    background: #fff5f5;
  }

  .ap__icon-btn--delete:hover {
    background: #fee2e2;
    border-color: #ef4444;
  }

  .ap__modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ap__modal {
    background: #fff;
    border-radius: 14px;
    padding: 32px 28px 24px;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 16px 48px rgba(0,0,0,0.18);
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .ap__modal--sm { max-width: 340px; }

  .ap__modal-title {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 4px;
  }

  .ap__modal-body {
    font-size: 14px;
    color: #666;
    margin: 0;
  }

  .ap__modal-label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
  }

  .ap__modal-required {
    color: #ef4444;
    margin-left: 2px;
  }

  .ap__modal-input {
    width: 100%;
    border: 1.5px solid #d1d5db;
    border-radius: 8px;
    padding: 9px 12px;
    font-size: 14px;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s;
  }

  .ap__modal-input:focus { border-color: #6366f1; }

  .ap__modal-upload-area {
    display: block;
    border: 2px dashed #d1d5db;
    border-radius: 10px;
    padding: 20px;
    cursor: pointer;
    text-align: center;
    transition: border-color 0.2s, background 0.2s;
  }

  .ap__modal-upload-area:hover {
    border-color: #1d4ed8;
    background: #eff6ff;
  }

  .ap__modal-upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    color: #6b7280;
    font-size: 13px;
  }

  .ap__modal-upload-icon {
    font-size: 24px;
    color: #9ca3af;
  }

  .ap__modal-upload-hint {
    font-size: 11px;
    color: #9ca3af;
  }

  .ap__modal-preview {
    width: 100%;
    max-height: 100px;
    object-fit: contain;
    border-radius: 8px;
  }

  .ap__modal-filename {
    font-size: 12px;
    color: #6b7280;
    margin: 0;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ap__modal-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    margin-top: 8px;
  }

  .ap__modal-btn {
    padding: 9px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: background 0.2s;
  }

  .ap__modal-btn--cancel { background: #f3f4f6; color: #374151; }
  .ap__modal-btn--cancel:hover { background: #e5e7eb; }
  .ap__modal-btn--save { background: #1d4ed8; color: #fff; }
  .ap__modal-btn--save:hover { background: #1e40af; }
  .ap__modal-btn--delete { background: #ef4444; color: #fff; }
  .ap__modal-btn--delete:hover { background: #dc2626; }
`;